import React from 'react';
import {
  Modal,
  Button,
  Group,
  Stack,
  Text,
  Code,
  Alert,
} from '@mantine/core';
import { IconAlertTriangle, IconTrash } from '@tabler/icons-react';
import { notifications } from '@mantine/notifications';
import { useDeleteSettingMutation } from 'generated/graphql';

interface SettingDeleteConfirmModalProps {
  opened: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  settingPath: string | null;
}

export function SettingDeleteConfirmModal({
  opened,
  onClose,
  onSuccess,
  settingPath,
}: SettingDeleteConfirmModalProps) {
  const [deleteSetting, { loading }] = useDeleteSettingMutation();

  const handleDelete = async () => {
    if (!settingPath) return;

    try {
      const result = await deleteSetting({
        variables: { path: settingPath },
      });

      if (result.data?.settings?.deleteSetting) {
        notifications.show({
          title: 'Setting Deleted',
          message: `Setting "${settingPath}" has been deleted.`,
          color: 'green',
        });
        onClose();
        onSuccess?.();
      } else {
        notifications.show({
          title: 'Delete Failed',
          message: 'The setting could not be found or was already deleted.',
          color: 'orange',
        });
        onClose();
      }
    } catch (error: any) {
      const message = error?.message || 'Failed to delete setting';
      notifications.show({
        title: 'Error',
        message,
        color: 'red',
      });
    }
  };

  return (
    <Modal
      opened={opened}
      onClose={onClose}
      title="Confirm Delete Setting"
      size="md"
    >
      <Stack gap="md">
        <Alert icon={<IconAlertTriangle size={16} />} color="red" variant="light">
          This action cannot be undone. The setting will be permanently deleted.
        </Alert>

        <Text>
          Are you sure you want to delete the following setting?
        </Text>

        <Code block>{settingPath}</Code>

        <Group justify="flex-end" mt="md">
          <Button variant="light" onClick={onClose} disabled={loading}>
            Cancel
          </Button>
          <Button
            color="red"
            onClick={handleDelete}
            loading={loading}
            leftSection={<IconTrash size={16} />}
          >
            Delete Setting
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
